import React from "react"
import { createContext, useState } from "react"
import { useQuery, useMutation, gql } from '@apollo/client'
import { useCookies } from 'react-cookie'
import { useCsEvent } from "../client/useEvents"

// Type definitions for the User object from GQL query
export interface UserAbonelik {
  status: string
  tier_name: string
}

export interface UserCollection {
  id: string
  title: string
}

export interface UserLimit {
  left: number
  resetAt: string
}

export interface UserDownloadLimit extends UserLimit {
  creditBalance: number
}

export interface UserAiChatLimit {
  left: number
  max: number
  resetAt: string | null
  hasAccess: boolean
}

export interface AiTarihciConversationListItem {
  id: string;
  title: string | null;
  preview: string | null;
  updatedAt: string;
}

export interface EmailNewsletterPreference {
  id: string
  slug: string
  name: string
  default_opt_in: boolean
  opted_in: boolean
}

export interface User {
  id: string
  username: string
  patreon_id: string | null
  impersonatedBy: string | null
  abonelik: UserAbonelik | null
  collections: UserCollection[]
  viewLimit: UserLimit
  downloadLimit: UserDownloadLimit
  aiChatLimit?: UserAiChatLimit
  aiTarihciConversations?: AiTarihciConversationListItem[]
  emailNewsletterPreferences?: EmailNewsletterPreference[]
}

const loggedInContext = createContext({});

const gqlQuery = gql`
  query me {
    me {
      id,
      username,
      patreon_id,
      impersonatedBy,
      abonelik {
        status,
        tier_name
      },
      collections {
        id,
        title
      },
      viewLimit {
        left
        resetAt
      },
      downloadLimit {
        left
        resetAt
        creditBalance
      }
      aiChatLimit {
        left
        max
        resetAt
        hasAccess
      }
      aiTarihciConversations {
        id
        title
        preview
        updatedAt
      }
      emailNewsletterPreferences {
        id
        slug
        name
        default_opt_in
        opted_in
      }
    }
  }
`
const patreonSilMutation = gql`
  mutation PatreonSil {
    patreonSil
  }
`

const LoggedInContextProvider = ({ children }) => {
  const [cookies, setCookie] = useCookies(['login_cookie'])
  const [loggedIn, setLoggedIn] = useState<boolean>(false)
  const [user, setUser] = useState<User | null>(null)
  const [isLoginOpen, setLoginOpen] = useState<boolean>(false)
  const [isSignupOpen, setSignupOpen] = useState<boolean>(false)
  const [loginCta, setLoginCta] = useState<React.ReactNode | null>(null)
  const fireCsEvent = useCsEvent()

  let { data, refetch, loading } = useQuery<{ me: User }>(gqlQuery, {ssr: false})

  const [patreonSilTrigger,
    {called: patreonSilCalled,
      loading: patreonSilLoading,
      data: patreonSilData,
      error: patreonSilError}
  ] = useMutation(patreonSilMutation, {
    onCompleted: (data) => {
      //set login cookie on success
      if (data?.patreonSil) {
        console.info("Patreon sil completed")
        refetch()
      }
    },
    errorPolicy: 'all'
  })

  console.debug("[LoggedInContextProvider]", "loading:", loading,
    "me:", data?.me)

  if (data?.me !== user) {
    console.debug("User değişti, state update yap")
    setLoggedIn(!!data?.me)
    setUser(data?.me!)
  }

  const openLoginModal = (cta) => {
    setLoginOpen(true)
    setSignupOpen(false)
    if (cta) {
      setLoginCta(cta)
    }
    fireCsEvent({
      category: "User",
      action: "Engagement",
      label: "Login modal acildi"
    })
  }

  const openSignupModal = () => {
    setLoginOpen(false)
    setSignupOpen(true)
    fireCsEvent({
      category: "User",
      action: "Engagement",
      label: "Signup modal acildi"
    })
  }

  const closeAllModals = () => {
    setLoginOpen(false)
    setSignupOpen(false)
    setLoginCta(null)
  }

  const logIn = (token) => {
    setCookie('login_cookie', token, {path: '/', maxAge: 3600*24*30})
    setLoggedIn(true)
    refetch()
  }

  const logOut = () => {
    setCookie('login_cookie', '', {path: '/', maxAge: 3600*24*30})
    setLoggedIn(false)
    setUser(null)
    refetch()
  }

  const patreonHesabiSil = () => {
    patreonSilTrigger()
  }

  const refreshUser = async () => {
    console.debug("[AuthContext] Refreshing user data")
    return refetch()
  }

  return (
    <loggedInContext.Provider
      value={{ loggedIn, setLoggedIn,
        loginCta, setLoginCta,
        user, loading,
        logIn, logOut, patreonHesabiSil, refreshUser,
        isLoginOpen, isSignupOpen, openLoginModal,
        openSignupModal, closeAllModals
      }}>
      {children}
    </loggedInContext.Provider>
  );
};

export { loggedInContext, LoggedInContextProvider }
